import { buildHistoryParams, buildParams } from "./dashboardParams.js";
import actionPlansService from "./actionPlansService.js";
import benchmarkService from "./benchmarkService.js";
import chatsService from "./chatsService.js";
import cooperacaoService from "./cooperacaoService.js";
import driverEssenceService from "./driverEssenceService.js";
import driverMetricService from "./driverMetricService.js";
import driverMetricsMonthlyService from "./driverMetricsMonthlyService.js";
import driverMonthlyService from "./driverMonthlyService.js";
import driverService from "./driverService.js";
import insightService from "./insightService.js";
import journeyService from "./journeyService.js";
import metricService from "./metricService.js";
import overviewmetricService from "./overviewmetricService.js";
import periodService from "./periodService.js";
import respondentsMonthlyService from "./respondentsMonthlyService.js";
import segmentationService from "./segmentationService.js";
import verbatinsService from "./verbatinsService.js";

export const buildRespondentsMonthlyParams = (filters) => {
  const fk_id_empresa =
    filters?.fk_id_empresa || localStorage.getItem("tenant_id") || undefined;

  const generalPeriodIds = (filters?.periods ?? [])
    .filter(
      (p) =>
        (!p.city || p.city === "") &&
        (!p.nps_segment || p.nps_segment === "") &&
        (!p.client_type || p.client_type === ""),
    )
    .map((p) => p.id)
    .filter(Boolean);

  return {
    fk_id_empresa,
    period_ids: generalPeriodIds.length
      ? generalPeriodIds.join(",")
      : undefined,
  };
};

const normalizeCooperacaoItem = (item) => {
  const createdAt = item.created_at || item.createdAt;
  const postadoEm =
    item.postadoEm || item.postadoEm === "" ? item.postadoEm : createdAt;
  return {
    ...item,
    postadoEm: postadoEm ? String(postadoEm) : undefined,
  };
};

const normalizeCooperacaoPage = (page) => {
  if (!page || !Array.isArray(page.data)) return page;
  return {
    ...page,
    data: page.data.map(normalizeCooperacaoItem),
  };
};

// Reexportados para quem já importava daqui (ex.: xlsxExportService).
export { buildHistoryParams, buildParams };

export const dashboardService = {
  async getActionPlan(filters) {
    const res = await actionPlansService.get(filters);
    return res;
  },

  async getBenchmarking(filters) {
    const res = await benchmarkService.get(buildParams(filters));
    return res;
  },

  async getCooperacao() {
    const res = await cooperacaoService.get();
    return normalizeCooperacaoPage(res);
  },

  async getChats(filters) {
    const res = await chatsService.get(filters);
    return res;
  },

  async getDrivers(filters) {
    const res = await driverService.get(buildParams(filters));
    return res;
  },

  async getDriversAll() {
    const res = await driverService.get({});
    return res;
  },

  async getDriversEssence() {
    const res = await driverEssenceService.get({});
    return res;
  },

  async getDriversMetrics(filters) {
    const res = await driverMetricService.get(buildParams(filters));
    return res;
  },

  async getDriversMetricsHistory() {
    const res = await driverMetricService.get({});
    return res;
  },

  // Fonte direta do score histórico por etapa (CSAT/CES), sem depender de
  // haver driver (causa qualitativa) vinculado ao período — /drivers-metrics
  // só retorna uma etapa+período se algum driver foi taggeado ali, o que
  // deixa a aba Tendências com etapas sumindo quando a extração de causas
  // não rodou. pageSize no teto permitido pelo backend (MAX_PAGE_SIZE) pra
  // reduzir o risco do mesmo corte silencioso que afetava /drivers-metrics.
  async getMetricsHistory(filters) {
    const res = await metricService.get({
      pageSize: 100,
      ...buildHistoryParams(filters),
    });
    return res;
  },

  async getSegmentationHistory(filters) {
    const res = await segmentationService.get(buildHistoryParams(filters));
    return res;
  },

  async getDriversMetricsMonthly() {
    const res = await driverMetricsMonthlyService.get({});
    return res;
  },

  async getDriversMonthly() {
    const res = await driverMonthlyService.get({});
    return res;
  },

  async getInsights(filters) {
    const res = await insightService.get(buildParams(filters));
    return res;
  },

  async getJourney(filters) {
    const res = await journeyService.get(buildParams(filters));
    return res;
  },

  async getPeriods() {
    let page = 1;
    let all = [];
    let lastRes;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const res = await periodService.get({ page, pageSize: 100 });
      lastRes = res;
      const rows = Array.isArray(res?.data) ? res.data : [];
      all = all.concat(rows);
      if (!res?.pagination?.hasNext) break;
      page += 1;
    }
    return { ...lastRes, data: all };
  },

  async getOverview(filters) {
    const res = await overviewmetricService.get(buildParams(filters));
    return res;
  },

  async getOverviewHistory(filters) {
    const res = await overviewmetricService.get(buildHistoryParams(filters));
    return res;
  },

  async getRespondentsMonthly(filters) {
    const res = await respondentsMonthlyService.get(
      buildRespondentsMonthlyParams(filters),
    );
    return res;
  },

  async getSegmentation(filters) {
    const res = await segmentationService.get(buildParams(filters));
    return res;
  },

  async getTrends(filters) {
    const res = await metricService.get(filters);
    return res;
  },

  async getVerbatins(filters) {
    const res = await verbatinsService.get(filters);
    return res;
  },

  // Ações
  async createActionPlan(payload) {
    const res = await actionPlansService.post(payload);
    return res;
  },
  async updateActionPlan(id, payload) {
    const res = await actionPlansService.put(id, payload);
    return res;
  },

  async publishToCooperacao(payload) {
    const res = await cooperacaoService.post(payload);
    return res?.data ?? res;
  },

  async commentOnCooperacao(id, payload) {
    const res = await cooperacaoService.comment(id, payload);
    return res?.data ?? res;
  },

  async likeCooperacao(id) {
    const res = await cooperacaoService.like(id);
    return res?.data ?? res;
  },

  async reuseCooperacao(id) {
    const res = await cooperacaoService.reuse(id);
    return res?.data ?? res;
  },

  async updateJourney(id, payload) {
    const res = await journeyService.put(id, payload);
    return res;
  },

  async updateOverview(id, payload) {
    const res = await overviewmetricService.put(id, payload);
    return res;
  },
};
